import {
  FormControl,
  FormHelperText,
  InputLabel,
  RadioGroup,
} from "@mui/material";
import {
  Children,
  cloneElement,
  isValidElement,
  PropsWithChildren,
  ReactNode,
} from "react";
import { Control, Controller, FieldValues, Path } from "react-hook-form";
import { describedBy } from "./describedBy.ts";

export const RadioGroupFormField = <
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
>({
  control,
  name,
  label,
  ariaDescribedBy,
  children,
}: PropsWithChildren<{
  control: Control<TFieldValues>;
  name: TName;
  label: ReactNode;
  ariaDescribedBy?: string;
}>) => {
  const labelId = `radio-buttons-${name}`;
  const helperTextId = `helper-text-${name}`;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <FormControl
          component="fieldset"
          error={!!fieldState.error}
          aria-disabled={field.disabled}
          fullWidth
        >
          <InputLabel component="legend" id={labelId} shrink required>
            {label}
          </InputLabel>
          <RadioGroup
            aria-labelledby={labelId}
            aria-describedby={describedBy(ariaDescribedBy, helperTextId)}
            aria-disabled={field.disabled}
            name={field.name}
            value={field.value}
            onChange={(_, value) => field.onChange(value)}
            row
            sx={{ p: 1 }}
          >
            {!field.disabled
              ? children
              : Children.map(children, (child) =>
                  isValidElement(child)
                    ? cloneElement(child, { disabled: true } as never)
                    : child,
                )}
          </RadioGroup>
          <FormHelperText id={helperTextId}>
            {fieldState.error?.message || ""}
          </FormHelperText>
        </FormControl>
      )}
    />
  );
};
